import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from 'react-hook-form';
import { TextField } from '@mui/material';

export interface RHFTextFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date';
  placeholder?: string;
  /** Shows the required asterisk on the label (validation itself comes from the schema). */
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Reusable controlled MUI TextField bound to react-hook-form via `useController`.
 *
 * The repo's 63 forms each inline `Controller` + `TextField`; this is the shared
 * primitive that abstraction was missing — pass `control` + `name` and it wires
 * value/onChange/onBlur/ref and surfaces the zod error as helperText.
 */
export function RHFTextField<T extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  placeholder,
  required,
  disabled,
  fullWidth = true,
  size = 'small',
}: RHFTextFieldProps<T>) {
  const { field, fieldState } = useController({ name, control });

  return (
    <TextField
      {...field}
      label={label}
      type={type}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      error={!!fieldState.error}
      helperText={fieldState.error?.message}
      slotProps={type === 'date' ? { inputLabel: { shrink: true } } : undefined}
    />
  );
}
